import { hashString } from "@/features/petFireflies/flightGeometry";
import {
  PetHeaderFireflyPlacement,
  PetHeaderSticker,
  SceneSize,
  SceneTransform,
} from "./types";

export interface HeaderExcursion {
  x: number;
  y: number;
  viaX: number;
  viaY: number;
  gaze: { x: number; y: number };
}
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Bounded, purely visual offsets in canvas units; saved geometry is untouched. */
export function getHeaderExcursion(
  firefly: PetHeaderFireflyPlacement,
  stickers: PetHeaderSticker[],
  canvas: SceneSize,
  viewport: SceneSize,
  transform: SceneTransform
): HeaderExcursion | null {
  const size = firefly.width * canvas.width;
  const left = firefly.x * canvas.width,
    top = firefly.y * canvas.height;
  const bounds = {
    left: -transform.offsetX / transform.scale,
    top: -transform.offsetY / transform.scale,
    right: (viewport.width - transform.offsetX) / transform.scale,
    bottom: (viewport.height - transform.offsetY) / transform.scale,
  };
  // Do not pull cropped placements into view or move their saved origins.
  if (
    left < bounds.left ||
    top < bounds.top ||
    left + size > bounds.right ||
    top + size > bounds.bottom
  )
    return null;
  const center = { x: left + size / 2, y: top + size / 2 };
  const neighbors = stickers
    .map((sticker) => ({
      x: (sticker.x + sticker.width / 2) * canvas.width,
      y:
        sticker.y * canvas.height +
        (sticker.width * canvas.width * sticker.asset.imageHeight) /
          sticker.asset.imageWidth /
          2,
    }))
    .filter(
      (point) =>
        point.x >= bounds.left &&
        point.x <= bounds.right &&
        point.y >= bounds.top &&
        point.y <= bounds.bottom
    )
    .sort(
      (a, b) =>
        Math.hypot(a.x - center.x, a.y - center.y) -
        Math.hypot(b.x - center.x, b.y - center.y)
    );
  const seed = hashString(firefly.id);
  const neighbor = neighbors[0];
  const dx = neighbor
    ? neighbor.x - center.x
    : (seed % 2 ? 24 : -24) / transform.scale;
  const dy = neighbor ? neighbor.y - center.y : -18 / transform.scale;
  const length = Math.hypot(dx, dy);
  if (length < 1) return null;
  const factor = Math.min(0.55, 64 / (transform.scale * length));
  const limitX = (value: number) =>
    clamp(value, bounds.left - left, bounds.right - left - size);
  const limitY = (value: number) =>
    clamp(value, bounds.top - top, bounds.bottom - top - size);
  const x = limitX(dx * factor),
    y = limitY(dy * factor);
  if (Math.hypot(x, y) < 1) return null;
  return {
    x,
    y,
    viaX: limitX(x * 0.5 - ((dy / length) * 6) / transform.scale),
    viaY: limitY(y * 0.5 + ((dx / length) * 6) / transform.scale),
    gaze: { x: (dx / length) * 2.4, y: (dy / length) * 1.6 },
  };
}
